<template>
  <div class="container mt-5">
    <div class="calendar-header">
      <h2>{{ $t('schedulesCalendar.lessonSchedules') }}</h2>
      <div class="calendar-actions">
        <button v-if="isAdmin" class="btn-primary" @click="openForm">
          <i class="fas fa-plus"></i> {{ $t('schedulesCalendar.addLesson') }}
        </button>
        <button v-if="isTeacher || isSeniorTeacher" class="btn-primary" @click="openAvailableTimeForm">
          <i class="fas fa-clock"></i> {{ $t('schedulesCalendar.addAvailableTime') }}
        </button>
      </div>
    </div>
    <vue-cal
      ref="vueCal"
      :events="events"
      @view-change="handleWeekChange"
      :event-style="eventStyle"
      style="margin: 0 auto;"
      @time-cell-hover="onTimeCellHover"
      @time-cell-leave="onTimeCellLeave"
      :time-from="7 * 60"
      show-time-in-cells
      :selected-date="selectedDate"
      @event-click="onEventClick"
       @cell-click="onCellClick"
      :locale="currentLocale"
      clickable-cells
      :time-cell-height="60"
    ></vue-cal>

    <div class="overlay" v-if="isFormVisible">
      <div class="form-popup">
        <h2>{{ $t('schedulesCalendar.addLesson') }}</h2>
        <form @submit.prevent="addLesson">
          <div class="mb-3">
            <label for="lessonTitle" class="form-label">{{ $t('schedulesCalendar.title') }}:</label>
            <input type="text" class="form-control" v-model="newLesson.title" required>
          </div>
          <div class="mb-3">
            <label for="billingPlan" class="form-label">{{ $t('schedulesCalendar.billingPlan') }}:</label>
            <select class="form-control" v-model="newLesson.billingPlanId" required>
              <option v-for="plan in billingPlans" :key="plan.id" :value="plan.id">
                {{ plan.plan_name }}
              </option>
            </select>
          </div>
          <div class="mb-3">
            <label for="lessonDate" class="form-label">{{ $t('schedulesCalendar.date') }}:</label>
            <flat-pickr
              v-model="newLesson.date"
              :config="datePickerConfig"
              class="form-control"
              :placeholder="$t('schedulesCalendar.selectDate')"
              required
            ></flat-pickr>
          </div>
          <div class="mb-3">
            <label for="startTime" class="form-label">{{ $t('schedulesCalendar.startTime') }}:</label>
            <flat-pickr
              v-model="newLesson.startTime"
              :config="timePickerConfig"
              class="form-control"
              :placeholder="$t('schedulesCalendar.selectStartTime')"
              required
            ></flat-pickr>
          </div>
          <div class="mb-3">
            <label for="endTime" class="form-label">{{ $t('schedulesCalendar.endTime') }}:</label>
            <flat-pickr
              v-model="newLesson.endTime"
              :config="timePickerConfig"
              class="form-control"
              :placeholder="$t('schedulesCalendar.selectEndTime')"
              required
            ></flat-pickr>
          </div>
          <div class="mb-3">
            <label for="teacher" class="form-label">{{ $t('schedulesCalendar.teacher') }}:</label>
            <input type="text" 
                   class="form-control" 
                   v-model="newLesson.teacherName"
                   @input="fetchTeachers()" 
                   required>
            <div v-if="teacherSuggestions.length > 0" class="dropdown-suggestions">
              <ul>
                <li v-for="teacher in teacherSuggestions" 
                    :key="teacher.id" 
                    @click="selectTeacher(teacher)">
                  {{ teacher.name }}
                </li>
              </ul>
            </div>
          </div>
          <div class="mb-3">
            <label for="studygroup" class="form-label">{{ $t('schedulesCalendar.studyGroup') }}:</label>
            <input type="text" 
                  class="form-control" 
                  v-model="newLesson.studyGroupName"
                  @input="fetchStudyGroups" 
                  required>
            <div v-if="studyGroupSuggestions.length > 0" class="dropdown-suggestions">
              <ul>
                <li v-for="group in studyGroupSuggestions" 
                    :key="group.id" 
                    @click="selectStudyGroup(group)">
                  {{ group.group_name }}
                </li>
              </ul>
            </div>
          </div>
          <div class="mb-3">
            <label>{{ $t('schedulesCalendar.lessonType') }}:</label>
            <div>
              <input type="radio" id="single" value="single" v-model="lessonType" />
              <label for="single">{{ $t('schedulesCalendar.singleLesson') }}</label>
              <input type="radio" id="regular" value="regular" v-model="lessonType" />
              <label for="regular">{{ $t('schedulesCalendar.regularLesson') }}</label>
            </div>
          </div>

          <div class="mb-3" v-if="lessonType === 'regular'">
            <label for="numberOfLessons" class="form-label">{{ $t('schedulesCalendar.numberOfLessons') }}:</label>
            <input type="number" class="form-control" v-model="numberOfLessons" min="1" required />
          </div>
          <button type="submit" class="btn btn-primary">{{ $t('schedulesCalendar.addLesson') }}</button>
          <button type="button" class="btn btn-secondary" @click="closeForm">{{ $t('schedulesCalendar.close') }}</button>
        </form>
      </div>
    </div>

    <div class="overlay" v-if="isAvailableTimeFormVisible">
      <div class="form-popup">
        <h2>{{ $t('schedulesCalendar.addAvailableTime') }}</h2>
        <form @submit.prevent="addAvailableTime">
          <div class="mb-3">
            <label for="day" class="form-label">{{ $t('schedulesCalendar.day') }}:</label>
            <select class="form-control" v-model="newAvailableTime.day" required>
              <option v-for="day in daysOfWeek" :key="day" :value="day">{{ day }}</option>
            </select>
          </div>
          <div class="mb-3">
            <label for="startTime" class="form-label">{{ $t('schedulesCalendar.startTime') }}:</label>
            <flat-pickr
              v-model="newAvailableTime.startTime"
              :config="timePickerConfig"
              class="form-control"
              :placeholder="$t('schedulesCalendar.selectStartTime')"
              required
            ></flat-pickr>
          </div>
          <div class="mb-3">
            <label for="endTime" class="form-label">{{ $t('schedulesCalendar.endTime') }}:</label>
            <flat-pickr
              v-model="newAvailableTime.endTime"
              :config="timePickerConfig"
              class="form-control"
              :placeholder="$t('schedulesCalendar.selectEndTime')"
              required
            ></flat-pickr>
          </div>
          <button type="submit" class="btn btn-primary">{{ $t('schedulesCalendar.addAvailableTime') }}</button>
          <button type="button" class="btn btn-secondary" @click="closeAvailableTimeForm">{{ $t('schedulesCalendar.close') }}</button>
        </form>
      </div>
    </div>
    <!-- Available Time Edit Form -->
    <div class="overlay" v-if="showAvailableTimeEditForm">
      <div class="form-popup">
        <h2>{{ $t('schedulesCalendar.editAvailableTime') || 'Edit Available Time Slot' }}</h2>
        <form @submit.prevent="updateAvailableTimeSlot">
          <div class="mb-3">
            <label class="form-label">{{ $t('schedulesCalendar.startTime') || 'Start Time' }}:</label>
            <flat-pickr
              v-model="editingAvailableTime.start_time"
              :config="timePickerConfig"
              class="form-control"
              :placeholder="$t('schedulesCalendar.selectStartTime') || 'Select start time'"
              required
            ></flat-pickr>
          </div>
          <div class="mb-3">
            <label class="form-label">{{ $t('schedulesCalendar.endTime') || 'End Time' }}:</label>
            <flat-pickr
              v-model="editingAvailableTime.end_time"
              :config="timePickerConfig"
              class="form-control"
              :placeholder="$t('schedulesCalendar.selectEndTime') || 'Select end time'"
              required
            ></flat-pickr>
          </div>
          <div class="form-actions">
            <button type="submit" class="btn btn-primary">{{ $t('schedulesCalendar.update') || 'Update' }}</button>
            <button type="button" class="btn btn-danger" @click="deleteAvailableTimeSlot">{{ $t('schedulesCalendar.delete') || 'Delete' }}</button>
            <button type="button" class="btn btn-secondary" @click="showAvailableTimeEditForm = false">{{ $t('schedulesCalendar.cancel') || 'Cancel' }}</button>
          </div>
        </form>
      </div>
    </div>
    <div class="overlay" v-if="isViewLessonFormVisible">
      <div class="form-popup">
        <h2>{{ $t('schedulesCalendar.lessonDetails') }}</h2>
        <div v-if="selectedEventData">
          <p><b>{{ $t('schedulesCalendar.studyGroup') }}:</b> {{ selectedEventData.studyGroupName }}</p>
          <div class="mb-3">
            <label for="billingPlan" class="form-label">{{ $t('schedulesCalendar.billingPlan') }}:</label>
            <input type="text" class="form-control" v-model="selectedEventData.billingPlanName" disabled />
          </div>
          <div class="mb-3">
            <label for="teacher" class="form-label">{{ $t('schedulesCalendar.teacher') }}:</label>
            <input type="text" class="form-control" v-model="selectedEventData.teacherName" @input="fetchTeachers(true)" :disabled="!isAdmin" />
            <div v-if="teacherSuggestions.length > 0" class="dropdown-suggestions">
              <ul>
                <li v-for="teacher in teacherSuggestions" :key="teacher.id" @click="selectTeacher(teacher, true)">
                  {{ teacher.name }}
                </li>
              </ul>
            </div>
            <div v-if="!isFetchingEventData && teacherError" style="color: red;">
              {{ $t('schedulesCalendar.noTeacherFound') }}
            </div>
          </div>
          <div class="mb-3">
            <label for="lessonDate" class="form-label">{{ $t('schedulesCalendar.date') }}:</label>
            <flat-pickr
              v-model="newLesson.date"
              :config="datePickerConfig"
              class="form-control"
              :placeholder="$t('schedulesCalendar.selectDate')"
              required
              :disabled="isDateTimeFieldDisabled"
            ></flat-pickr>
          </div>
          <div class="mb-3">
            <label for="startTime" class="form-label">{{ $t('schedulesCalendar.startTime') }}:</label>
            <flat-pickr
              v-model="newLesson.startTime"
              :config="timePickerConfig"
              class="form-control"
              :placeholder="$t('schedulesCalendar.selectStartTime')"
              required
              :disabled="isDateTimeFieldDisabled"
            ></flat-pickr>
          </div>
          <div class="mb-3">
            <label for="endTime" class="form-label">{{ $t('schedulesCalendar.endTime') }}:</label>
            <flat-pickr
              v-model="newLesson.endTime"
              :config="timePickerConfig"
              class="form-control"
              :placeholder="$t('schedulesCalendar.selectEndTime')"
              required
              :disabled="isDateTimeFieldDisabled"
            ></flat-pickr>
            <div v-if="endTimeError" style="color: red;">
              {{ $t('schedulesCalendar.endTimeError') }}
            </div>
          </div>
          <div class="mb-3">
            <label>{{ $t('schedulesCalendar.lessonHappened') }}:</label>
            <div>
              <input 
                type="checkbox" 
                id="lessonHappenedYes" 
                ref="lessonHappenedYes" 
                :checked="selectedEventData.lesson_completed === true" 
                @change="selectLessonHappened('yes')" 
                :disabled="!isLessonEndTimeReached" 
              />
              <label for="lessonHappenedYes">{{ $t('schedulesCalendar.yes') }}</label>
              <input 
                type="checkbox" 
                id="lessonHappenedNo" 
                ref="lessonHappenedNo" 
                :checked="selectedEventData.lesson_completed === false" 
                @change="selectLessonHappened('no')" 
                :disabled="!isLessonEndTimeReached" 
              />
              <label for="lessonHappenedNo">{{ $t('schedulesCalendar.no') }}</label>
              <div v-if="checkboxError" style="color: red;">
                {{ $t('schedulesCalendar.lessonHappenedError') }}
              </div>
              <div v-if="!isLessonEndTimeReached" style="color: blue; font-size: 0.8em;">
                {{ $t('schedulesCalendar.lessonNotEndedYet') || 'Lesson status can be updated only after the scheduled end time' }}
              </div>
            </div>
          </div>
          <div v-if="selectedEventData">
            <h3>{{ $t('schedulesCalendar.students') }}:</h3>
            <ul>
              <li v-for="student in selectedEventData.students" :key="student.id">
                <input type="checkbox" v-model="student.attended" :disabled="!student.editable" /> <!-- Checkbox for attendance -->
                {{ student.first_name }} {{ student.last_name }}
              </li>
            </ul>
          </div>
          <div v-else>
            <p>{{ $t('schedulesCalendar.noStudentsFound') }}</p>
          </div>
        </div>
        <div v-else>
          <p>{{ $t('schedulesCalendar.loadingLessonDetails') }}</p>
        </div>
        <button type="button" class="btn btn-primary" @click="submitChanges" :disabled="endTimeError || checkboxError || teacherError">{{ $t('schedulesCalendar.submitChanges') }}</button>
        <button type="button" class="btn btn-secondary" @click="closeViewLessonForm">{{ $t('schedulesCalendar.close') }}</button>
        <button type="button" class="btn btn-danger" @click="deleteLesson" v-if="isAdmin">{{ $t('schedulesCalendar.deleteLesson') }}</button>
      </div>
    </div>
    <!-- Add this dialog component in your template section -->
    <div class="overlay" v-if="showRecurrenceDialog">
      <div class="form-popup recurrence-dialog">
        <h2>{{ recurrenceDialogTitle }}</h2>
        
        <div class="recurrence-options">
          <div class="option" @click="handleRecurrenceOption('single')">
            <div class="option-icon">
              <i class="fas fa-calendar-day"></i>
            </div>
            <div class="option-content">
              <h4>{{ isDeleting ? $t('schedulesCalendar.deleteThisOnly') || 'Delete only this event' : $t('schedulesCalendar.editThisOnly') || 'Edit only this event' }}</h4>
              <p>{{ $t('schedulesCalendar.onlyThisEventWillBeAffected') || 'Only this single event will be changed' }}</p>
            </div>
          </div>
          
          <div class="option" @click="handleRecurrenceOption('following')">
            <div class="option-icon">
              <i class="fas fa-calendar-alt"></i>
            </div>
            <div class="option-content">
              <h4>{{ isDeleting ? $t('schedulesCalendar.deleteThisAndFollowing') || 'Delete this and following events' : $t('schedulesCalendar.editThisAndFollowing') || 'Edit this and following events' }}</h4>
              <p>{{ $t('schedulesCalendar.thisAndFutureEventsWillBeAffected') || 'This and all future events with the same pattern will be changed' }}</p>
            </div>
          </div>
          
          <div class="option" @click="closeRecurrenceDialog">
            <div class="option-icon">
              <i class="fas fa-times-circle"></i>
            </div>
            <div class="option-content">
              <h4>{{ $t('schedulesCalendar.cancel') || 'Cancel' }}</h4>
              <p>{{ $t('schedulesCalendar.noChangesWillBeMade') || 'No changes will be made' }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axiosInstance from '../router/axiosSetup';
import VueCal from 'vue-cal';
import 'vue-cal/dist/vuecal.css';
import FlatPickr from 'vue-flatpickr-component';
import 'flatpickr/dist/flatpickr.css';

import 'flatpickr/dist/l10n/uk.js';
import 'flatpickr/dist/l10n/pl.js';

export default {
  components: {
    VueCal,
    FlatPickr,
  },
  props: {
    viewAsTeacher: {
      type: Boolean,
      default: false
    },
    selectedTeacher: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      events: [], 
      selectedDate: '',
      newLesson: {
        title: '',
        date: '',
        startTime: '', // Changed from time to startTime
        endTime: '',
        teacherName: '', // For teacher input
        studyGroupName: '',
        billingPlanId: null,
      },
      isFormVisible: false,
      currentWeekStart: null,
      currentWeekEnd: null,
      hoveredTimeCell: null,
       teacherSuggestions: [],
       studyGroupSuggestions: [],
       lessonType: 'single', // Default to single lesson selection
      numberOfLessons: 1,
      isViewLessonFormVisible: false,
    selectedEvent: null,
    selectedEventData: null,
    hasCheckedStatus: false ,
    selectedLessonDate: null,
    isFetchingEventData: true,
    endTimeError: false,
    checkboxError: false,
    teacherError: false,
    pickedTeacherID: null,
    showAvailableTimeEditForm: false,
    showRecurrenceDialog: false,
    isDeleting: false,
    pendingOperation: null,
    pendingEventData: null,
    pendingEventType: null, // 'lesson' or 'availableTime'
    recurrenceType: null,
    notificationVisible: false,
    notificationType: 'success',
    notificationMessage: '',
    editingAvailableTime: {
      id: null,
      date: '',
      start_time: '',
      end_time: '',
      teacherId: ''
    },
    newAvailableTime: {
        day: '',
        startTime: '',
        endTime: '',
    },
    isAvailableTimeFormVisible: false,
    daysOfWeek: [
      this.$t('schedulesCalendar.monday'),
      this.$t('schedulesCalendar.tuesday'),
      this.$t('schedulesCalendar.wednesday'),
      this.$t('schedulesCalendar.thursday'),
      this.$t('schedulesCalendar.friday'),
      this.$t('schedulesCalendar.saturday'),
      this.$t('schedulesCalendar.sunday')
    ],
    eventStyle(event) {
      if (event.title === 'Free Time Slot') {
        return {
          backgroundColor: '#FFC0CB',
          color: '#000000',
          border: '1px dashed #FF69B4',
          opacity: 0.7
        };
      }
      
      // Regular lesson styling logic
      const now = new Date();
      const eventEndTime = new Date(event.end);
      
      if (eventEndTime > now) {
        return { backgroundColor: '#007BFF', color: '#000000' }; // Future lessons
      } else if (event.lesson_completed === false) {
        return { backgroundColor: '#DC3545', color: '#000000' }; // Incomplete lessons
      } else if (event.lesson_completed === true) {
        return { backgroundColor: '#28A745', color: '#000000' }; // Completed lessons
      }
      return { backgroundColor: '#FFC107', color: '#000000' }; // Unknown status
    },
    datePickerConfig: {
      dateFormat: 'd/m/Y',  // dd/mm/yyyy
      locale: this.$i18n.locale === 'uk' ? 'uk' : (this.$i18n.locale === 'ru' ? 'ru' : 'en'),
      firstDayOfWeek: 1,  // 1 = Monday (0 is Sunday)
    },
    timePickerConfig: {
      enableTime: true,
      noCalendar: true,
      time_24hr: true,  // 24-hour time
      dateFormat: 'H:i', // 24-hour format
      locale: this.$i18n.locale === 'uk' ? 'uk' : (this.$i18n.locale === 'ru' ? 'ru' : 'en')
    },
      currentLocale: this.$i18n.locale,
    };
  },
  watch: {
    viewAsTeacher: {
      handler() {
        this.fetchLessonsForCurrentWeek();
      },
      immediate: true // Fetch lessons immediately if viewAsTeacher is true on mount
    },
    selectedTeacher: {
      handler() {
        this.fetchLessonsForCurrentWeek();
      },
      immediate: true
    },
    events: {
      handler() {
          this.$nextTick(() => this.styleEvents())
      },
      deep: true // Watch for changes within the array
    },
    '$i18n.locale': {
      handler(newLocale) {
        this.currentLocale = newLocale; // Update the locale when it changes

        const flatpickrLocale = newLocale === 'uk' ? 'uk' : (newLocale === 'ru' ? 'ru' : 'en');
        this.datePickerConfig = {
          ...this.datePickerConfig,
          locale: flatpickrLocale
        };
        this.timePickerConfig = {
          ...this.timePickerConfig,
          locale: flatpickrLocale
        };
        
        // Update days of week when locale changes
        this.daysOfWeek = [
          this.$t('schedulesCalendar.monday'),
          this.$t('schedulesCalendar.tuesday'),
          this.$t('schedulesCalendar.wednesday'),
          this.$t('schedulesCalendar.thursday'),
          this.$t('schedulesCalendar.friday'),
          this.$t('schedulesCalendar.saturday'),
          this.$t('schedulesCalendar.sunday')
        ];
      },
      immediate: true,
    },
  },
  computed: {
    isAdmin() {
      return localStorage.getItem('role') === 'admin' && !this.viewAsTeacher;
    },
    isTeacher() {
      return localStorage.getItem('role') === 'teacher';
    },
    isSeniorTeacher() {
      return localStorage.getItem('role') === 'senior_teacher';
    },
    isDateTimeFieldDisabled() {
      // Allow both admins and senior teachers to edit date/time fields
      return !(this.isAdmin || this.isSeniorTeacher);
    },
    isLessonEndTimeReached() {
      if (!this.selectedEventData) return false;
      
      // Get current time
      const now = new Date();
      
      // Create a date object from the lesson's date and end time
      const lessonDate = this.selectedLessonDate;
      const endTime = this.selectedEventData.end;
      
      if (!lessonDate || !endTime) return false;
      
      // Parse the time to get hours and minutes
      const [hours, minutes] = endTime.split(':').map(Number);
      
      // Create a date object for the end time
      const lessonEndDateTime = new Date(lessonDate);
      lessonEndDateTime.setHours(hours, minutes, 0, 0);
      
      // Compare with current time
      return now >= lessonEndDateTime;
    },
    recurrenceDialogTitle() {
      const action = this.isDeleting ? 
        (this.$t('schedulesCalendar.deleteRecurringEvent') || 'Delete Recurring Event') : 
        (this.$t('schedulesCalendar.editRecurringEvent') || 'Edit Recurring Event');
        
      return action;
    }
    
  },
  methods: {
    deleteLesson() {
      this.pendingEventData = {...this.selectedEventData};
      this.pendingEventType = 'lesson';
      this.isDeleting = true;
      this.pendingOperation = 'delete';
      
      // Show the recurrence dialog
      this.showRecurrenceDialog = true;
    },
    async deleteLessonWithRecurrence() {
      try {
        await axiosInstance.delete(`/admin/delete-lesson/${this.pendingEventData.id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          params: {
            recurrenceType: this.recurrenceType
          }
        });
        
        this.isViewLessonFormVisible = false;
        await this.fetchLessonsForCurrentWeek();
      } catch (error) {
        console.error('Error deleting lesson:', error);
        alert('Error deleting lesson. Please try again.');
      }
    },
    checkboxValidation() {
        const { lessonHappened } = this.selectedEventData;
        // Check if lessonHappened is explicitly set to true or false.
        this.checkboxError = (lessonHappened === null || lessonHappened === false);
    },
    validateEndTime() {
        // Ensure that both times are set
        if (this.selectedEventData.start && this.selectedEventData.end) {
            // Get date objects for comparison (using current date for local context)
            const start = new Date();
            const end = new Date();
            const [startHours, startMinutes] = this.selectedEventData.start.split(':').map(Number);
            const [endHours, endMinutes] = this.selectedEventData.end.split(':').map(Number);
            start.setHours(startHours, startMinutes, 0);
            end.setHours(endHours, endMinutes, 0);
            
            // Validate time
            this.endTimeError = end <= start; // Set error if end time is not after start time
        } else {
            this.endTimeError = false; // Reset if times are not set
        }
    },
    selectLessonHappened(value) {
      if (value === 'yes') {
            this.selectedEventData.lesson_completed = true; // Set Yes
            this.hasCheckedStatus = true; // Mark as checked
            this.$refs.lessonHappenedNo.checked = false; // Uncheck No

            this.selectedEventData.students.forEach(student => {
              student.attended = true;
              student.editable = true;
            });
        } else if (value === 'no') {
            this.selectedEventData.lesson_completed = false; // Set No
            this.hasCheckedStatus = true; // Mark as checked
            this.$refs.lessonHappenedYes.checked = false; // Uncheck Yes
            this.selectedEventData.students.forEach(student => {
              student.attended = false;
              student.editable = false;
            });
        }

        // Automatically check the opposite checkbox if the current is unchecked
        if (value === 'yes' && !this.$refs.lessonHappenedYes.checked) {
            this.$refs.lessonHappenedNo.checked = true; // Check No if Yes is unchecked
            this.selectedEventData.lesson_completed = false;
        } else if (value === 'no' && !this.$refs.lessonHappenedNo.checked) {
            this.$refs.lessonHappenedYes.checked = true; // Check Yes if No is unchecked
            this.selectedEventData.lesson_completed = true;
        }
        this.hasCheckedStatus = this.$refs.lessonHappenedYes.checked || this.$refs.lessonHappenedNo.checked;
    },
    async fetchBillingPlans() {
      try {
        const response = await axiosInstance.get('/admin/all-billing-plans',{
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        this.billingPlans = response.data;
      } catch (error) {
        console.error('Error fetching billing plans:', error);
      }
    },
    async submitChanges() {
      if(this.isFetchingEventData) return;

      // Validate teacher
      if (!this.pickedTeacherID) {
        this.teacherError = true;
        return;
      }
      
      // Check general validation
      if (this.endTimeError || this.checkboxError || this.teacherError) {
        return;
      }
      
      // For non-admin users, ensure they don't modify time/date
      if (!this.isAdmin && !this.isSeniorTeacher) {
        // Use original date/time values from the event
        this.newLesson.date = this.originalEventData.date;
        this.newLesson.startTime = this.originalEventData.start;
        this.newLesson.endTime = this.originalEventData.end;
      }
      
      // If the lesson end time hasn't been reached, preserve the original lesson_completed status
      if (!this.isLessonEndTimeReached) {
        this.selectedEventData.lesson_completed = this.originalEventData.lesson_completed;
      } else if (!this.$refs.lessonHappenedNo.checked && !this.$refs.lessonHappenedYes.checked) {
        // Validate lesson completed status only if the end time has been reached
        alert('Please select Yes or No for "Lesson Happened".');
        return;
      }
      
  
      // Store the current event data for later use
      this.pendingEventData = {...this.selectedEventData};
      this.pendingEventType = 'lesson';
      this.isDeleting = false;
      this.pendingOperation = 'edit';
      
      // Show the recurrence dialog instead of updating immediately
      this.showRecurrenceDialog = true;
    },
    closeRecurrenceDialog() {
      this.showRecurrenceDialog = false;
      this.pendingEventData = null;
      this.pendingOperation = null;
      this.pendingEventType = null;
      this.recurrenceType = null;
    },

    handleRecurrenceOption(option) {
      this.recurrenceType = option; // 'single' or 'following'
      
      if (this.pendingOperation === 'edit') {
        if (this.pendingEventType === 'lesson') {
          this.updateLessonWithRecurrence();
        } else if (this.pendingEventType === 'availableTime') {
          this.updateAvailableTimeWithRecurrence();
        }
      } else if (this.pendingOperation === 'delete') {
        if (this.pendingEventType === 'lesson') {
          this.deleteLessonWithRecurrence();
        } else if (this.pendingEventType === 'availableTime') {
          this.deleteAvailableTimeWithRecurrence();
        }
      }
      
      this.closeRecurrenceDialog();
    },

    // Add method for updating lessons with recurrence
    async updateLessonWithRecurrence() {
      const { id } = this.pendingEventData;
      const dateParts = this.newLesson.date.split('/');
      const formattedDate = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]}`;

      // Use the new values from the form instead of the original values
      const updatedEventData = {
        id: id,
        title: this.pendingEventData.teacherName,
        studyGroup: this.pendingEventData.studyGroupId,
        teacher: this.pickedTeacherID, // Use the selected teacher ID
        start: `${formattedDate}T${this.newLesson.startTime}`, // Use form values
        end: `${formattedDate}T${this.newLesson.endTime}`, // Use form values
        lessonHappened: this.pendingEventData.lesson_completed,
        recurrenceType: this.recurrenceType
      };

      try {
        // Create a custom axios instance with a longer timeout for this specific request
        const customAxios = axiosInstance.create({
          timeout: 30000 // 30 seconds instead of 10
        });
        
        const response = await customAxios.post('/admin/update-lesson', updatedEventData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        
        if (response.status === 200) {
          const index = this.events.findIndex(event => event.id === id);
          if (index !== -1) {
            this.events[index].lessonHappened = updatedEventData.lessonHappened;
          }

          // Update attendance records
          await Promise.all(this.selectedEventData.students.map(student =>
            axiosInstance.post('/admin/update-attendance', {
              lesson_id: id,
              student_id: student.id,
              attended: student.attended
            }, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            })
          ));

          // Create transactions
          const previousAttendance = this.selectedEventData.students.map(student => ({
            id: student.id,
            attended: student.original_attended
          }));
          console.log('Previous Attendance:', previousAttendance);
          await axiosInstance.post('/admin/create-transactions', {
            lessonId: id,
            lessonHappened: this.selectedEventData.lesson_completed,
            previousLessonHappened: this.selectedEventData.original_lesson_completed,
            previousAttendance: previousAttendance
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          });
          
          this.isViewLessonFormVisible = false;
          await this.fetchLessonsForCurrentWeek();
          this.pickedTeacherID = null;
        }
      } catch (error) {
        console.error('Error updating lesson:', error);
        alert('Error updating lesson. Please try again.');
      }
    },
  onEventClick(event) {
    
    // Check if this is an available time slot using both potential ways it might be identified
    if (event.title === 'Free Time Slot' || 
        event.title === this.$t('schedulesCalendar.freeTimeSlot') ||
        event.class?.includes('available-time')) {
      this.editAvailableTimeSlot(event);
    } else {
      this.isViewLessonFormVisible = true;
      this.selectedEvent = event;
      this.$nextTick(() => {
        this.fetchEventData(event);
      });
    }
  },
  editAvailableTimeSlot(event) {
    // Log the event to see its structure
    
    // Handle both possible formats (string or Date object)
    let dateStr, startTime, endTime;
    
    if (typeof event.start === 'string') {
      // If it's a string like "2/25/2025 10:00"
      const startParts = event.start.split(' ');
      const endParts = event.end.split(' ');
      
      dateStr = startParts[0];
      startTime = startParts[1];
      endTime = endParts[1];
    } else if (event.start instanceof Date) {
      // If it's a Date object
      dateStr = event.start.toISOString().split('T')[0];
      startTime = event.start.getHours().toString().padStart(2, '0') + ':' + 
                  event.start.getMinutes().toString().padStart(2, '0');
      endTime = event.end.getHours().toString().padStart(2, '0') + ':' + 
                event.end.getMinutes().toString().padStart(2, '0');
    } else {
      // For other formats, try to extract what we need
      const startDate = new Date(event.start);
      const endDate = new Date(event.end);
      
      dateStr = startDate.toISOString().split('T')[0];
      startTime = startDate.getHours().toString().padStart(2, '0') + ':' + 
                  startDate.getMinutes().toString().padStart(2, '0');
      endTime = endDate.getHours().toString().padStart(2, '0') + ':' + 
                endDate.getMinutes().toString().padStart(2, '0');
    }
    
    this.editingAvailableTime = {
      id: event.id,
      date: dateStr,
      start_time: startTime,
      end_time: endTime,
      teacherId: localStorage.getItem('userId')
    };
    
    this.showAvailableTimeEditForm = true;
  },
      async fetchEventData(event) {
      this.isFetchingEventData = true;
      try {
          // Create formatted dates for the request
          const formattedStartDate = this.formatDate(event.start);
          const formattedEndDate = this.formatDate(event.end);
          
          const token = localStorage.getItem('token');

          const response = await axiosInstance.get('/admin/lessons', {
            headers: {
              Authorization: `Bearer ${token}`
            },
            params: {
              start: formattedStartDate,
              end: formattedEndDate
            }
          });
          
          // Check if any lessons are found in the provided range
          if (response.data.length > 0) {
              // Find the specific lesson by ID
              const lessonId = event.id;
              const lessonData = response.data.find(lesson => lesson.id === lessonId);
              const billingPlanName = await this.getBillingPlanName(lessonData.billing_plan_id);
          
              if (lessonData) {
                  const studyGroupName = await this.getStudyGroupName(lessonData.studygroup_id);
                  const lessonDate = new Date(lessonData.lesson_date);

                  // Format the date for display (dd/mm/yyyy)
                  const day = lessonDate.getDate().toString().padStart(2, '0');
                  const month = (lessonDate.getMonth() + 1).toString().padStart(2, '0');
                  const year = lessonDate.getFullYear();
                  const formattedDate = `${day}/${month}/${year}`;
                  
                  // Format times (HH:MM)
                  const startTime = lessonData.start_time.split('+')[0].slice(0, 5);
                  const endTime = lessonData.end_time.split('+')[0].slice(0, 5);
                  
                  // Set the date and time values for the form
                  this.newLesson.date = formattedDate;
                  this.newLesson.startTime = startTime;
                  this.newLesson.endTime = endTime;
                  
                  // Continue with setting up other lesson data
                  // ...existing code for setting selectedLessonDate...
                  
                  const lessonDate1 = new Date(lessonData.lesson_date);
                  const timezoneOffset = lessonDate1.getTimezoneOffset() / 60;
                  lessonDate1.setHours(lessonDate1.getHours() - timezoneOffset);
                  
                  // Format to YYYY-MM-DD for internal use
                  this.selectedLessonDate = lessonDate1.toISOString().split('T')[0];
                  
                  this.selectedEventData = {
                      teacherName: `${lessonData.first_name} ${lessonData.last_name}`,
                      start: startTime,
                      end: endTime,
                      studyGroupName: studyGroupName,
                      billingPlanName: billingPlanName,
                      lesson_completed: lessonData.lesson_completed,
                      original_lesson_completed: lessonData.lesson_completed,
                      studyGroupId: lessonData.studygroup_id,
                      date: formattedDate,
                      teacherId: lessonData.main_teacher_id,
                      id: lessonData.id,
                  };
                  
                  this.pickedTeacherID = this.selectedEventData.teacherId;
                  this.originalEventData = JSON.parse(JSON.stringify(this.selectedEventData));
                  
                  if (this.selectedEventData.lesson_completed === null) {
                      this.selectedEventData.lesson_completed = false;
                  }
                  
                  await this.fetchStudentsForSelectedGroup();
                  await this.fetchAttendanceForLesson(lessonData.id);
              } else {
                  console.warn('No matching lesson found for the clicked event.');
                  this.selectedEventData = null;
              }
          } else {
              this.selectedEventData = null;
              console.warn('No lessons found for this time slot.');
          }
          
      } catch (error) {
          console.error('Error fetching event data:', error);
          this.selectedEventData = null;
      } finally {
          this.isFetchingEventData = false;
      }
    },
  async getBillingPlanName(billingPlanId) {
    try {
      const response = await axiosInstance.get('/admin/billing-plans', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          id: billingPlanId
        }
      });
      
      return response.data[0].plan_name; // Return the billing plan name
    } catch (error) {
      console.error('Error fetching billing plan name:', error);
      return 'Unknown Billing Plan'; // Or handle the error appropriately
    }
  },
    async fetchStudentsForSelectedGroup() {
        if (this.selectedEventData && this.selectedEventData.studyGroupId) {
            try {
              const response = await axiosInstance.get('/admin/students', {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                },
                params: {
                  groupId: this.selectedEventData.studyGroupId
                }
              });
                this.selectedEventData.students = response.data.map(student => ({
                id: student.id,
                first_name: student.first_name,
                last_name: student.last_name,
                attended: true // Default value here; updating this in the next step with attendance
            }));
            } catch (error) {
                console.error('Error fetching students:', error);
                this.selectedEventData.students = []; //Handle the error
            }
        }
    },
    async fetchAttendanceForLesson(lessonId) {
      if (lessonId) {
        try {
          const response = await axiosInstance.get('/admin/attendance', {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            },
            params: {
              lesson_id: lessonId // Provide the lesson ID to fetch attendance
            }
          });

            // Link attendance status to the corresponding students
            const attendanceRecords = response.data;

            this.selectedEventData.students.forEach(student => {
              const attendanceRecord = attendanceRecords.find(record => record.student_id === student.id);
              const attendanceStatus = attendanceRecord ? attendanceRecord.attended : false;
              
              // Set both current and original attendance
              student.attended = attendanceStatus;
              student.original_attended = attendanceStatus; // Store original state
            });
        } catch (error) {
            console.error('Error fetching attendance records:', error);
        }
    }
  },
    closeViewLessonForm() {
    this.isViewLessonFormVisible = false;
    this.selectedEvent = null;
    this.selectedEventData = null;
    this.hasCheckedStatus = false;
    this.pickedTeacherID = null;
  },
  async getStudyGroupName(studyGroupId) {
    try {
      const response = await axiosInstance.get('/admin/studygroups', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          id: studyGroupId
        }
      });
        return response.data[0].group_name; // Return the group name
    } catch (error) {
        console.error('Error fetching study group name:', error);
        return 'Unknown Study Group'; // Or handle the error appropriately
    }
},
  styleEvents() {
    this.$nextTick(() => {
      this.events.forEach(eventData => {
        // Define the selector based on the event type
        let selector;
        
        if (eventData.title === 'Free Time Slot') {
          // For available time slots
          selector = `.available-time-${eventData.id}`;
        } else {
          // For lesson events
          selector = `.event-${eventData.id}`;
        }
        
        const eventEl = this.$refs.vueCal.$el.querySelector(selector);

        if (eventEl) {
          if (eventData.title === 'Free Time Slot') {
            // Style available time slots in light pink
            eventEl.style.backgroundColor = '#FFC0CB'; // Light pink color
            eventEl.style.color = '#000000'; // Black text for contrast
            eventEl.style.border = '1px dashed #FF69B4'; // Optional: add a dashed border
          } else {
            // Existing styling for lesson events
            const now = new Date();
            const eventEndTime = new Date(eventData.end);
            
            if (eventEndTime > now) {
              eventEl.style.backgroundColor = '#007BFF'; // Blue for future lessons
            } else if (eventData.lesson_completed === false) {
              eventEl.style.backgroundColor = '#DC3545'; // Red for incomplete past lessons
            } else if (eventData.lesson_completed === true) {
              eventEl.style.backgroundColor = '#28A745'; // Green for completed lessons
            } else {
              eventEl.style.backgroundColor = '#FFC107'; // Yellow for unknown status
            }
            eventEl.style.color = 'black';
          }
        }
      });
    });
  },
   async fetchStudyGroups() {
  const name = this.newLesson.studyGroupName;

  if (name.length >= 3) {
    try {
      const response = await axiosInstance.get('/admin/studygroups', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          name // Pass name for searching
        }
      });
      
      this.studyGroupSuggestions = response.data; // Set suggestions from the response
    } catch (error) {
      console.error('Error fetching study groups:', error);
    }
  } else {
    this.studyGroupSuggestions = []; // Clear suggestions if input is less than 3 characters
  }
},
async fetchTeachers(isEditing = false) {
        // Use the current context to determine which model needs to be updated
        const name = isEditing ? this.selectedEventData.teacherName : this.newLesson.teacherName; // Determine input context

        if (name.length >= 3) {
            try {
              const response = await axiosInstance.get('/admin/teachers', {
                headers: {
                  Authorization: `Bearer ${localStorage.getItem('token')}`
                },
                params: {
                  name // Using name as a query parameter
                }
              });

                const suggestions = response.data.map(teacher => ({
                    id: teacher.id,
                    name: `${teacher.first_name} ${teacher.last_name}`
                }));

                    this.teacherSuggestions = suggestions; // Set suggestions for editing
                    this.teacherError = this.teacherSuggestions.length === 0; // Set error state
            } catch (error) {
                console.error('Error fetching teachers:', error);
                // Clear suggestions and set error state
                    this.teacherSuggestions = [];
                    this.teacherError = true;
            }
        } else {
                this.teacherSuggestions = [];
                this.teacherError = false;
        }
    },
selectStudyGroup(group) {
  this.newLesson.studyGroupName = group.group_name; // This can be the display name
  this.studyGroupId = group.id; // Save the selected study group's ID
  this.studyGroupSuggestions = []; // Clear suggestions
},
  selectTeacher(teacher, isEditing = false) {
    if (isEditing) {
            this.selectedEventData.teacherName = teacher.name; // Set selected teacher name for editing
            this.selectedEventData.teacherId = teacher.id; // Store the selected teacher ID
        } else {
            this.newLesson.teacherName = teacher.name; // Set selected teacher name for adding
            this.newLesson.teacherId = teacher.id; // Store the selected teacher ID
           
            // Reset error when a valid selection is made
        }
        this.teacherError = false;
        
        this.teacherSuggestions = []; // Clear suggestions
        this.pickedTeacherID = teacher.id;
  },
     onTimeCellHover(cell) {
      this.hoveredTimeCell = cell; // Set to the hovered time cell state
    },
    onTimeCellLeave() {
      this.hoveredTimeCell = null; // Clear on mouse leave
    },
    getWeekStartAndEnd(date) {
    if (!date || !(date instanceof Date) || isNaN(date)) {
        console.error('Invalid date provided:', date);
        return; // Exit if the date is not valid
    }

    // Start with the input date
    const start = new Date(date);
    
    // Get the numeric day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const dayOfWeek = start.getDay(); 

    // Calculate how many days to subtract to get to the preceding Monday
    const daysToSubtract = (dayOfWeek + 6) % 7; // Ensures that if it's Sunday (0), we go back 6 days
    start.setDate(start.getDate() - daysToSubtract); // Adjust to Monday of the current week

    // Log calculated start date for validation

    // Calculate the end date (set to the following Sunday)
    const end = new Date(start);
    end.setDate(start.getDate() + 6); // Add 6 days to get Sunday

    // Format and save the week start and end dates while respecting local timezone
    this.currentWeekStart = this.formatDate(start); // Custom format YYYY-MM-DD
    this.currentWeekEnd = this.formatDate(end); // Format as YYYY-MM-DD

    // Log the final week range for verification
},

formatDate(date) {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Months are zero-based
    const day = date.getDate().toString().padStart(2, '0'); // Day of the month

    return `${year}-${month}-${day}`; // Format as YYYY-MM-DD
},
  async fetchLessonsForCurrentWeek() {
    if (this.currentWeekStart && this.currentWeekEnd) {
      try {
        const params = {
          start: this.currentWeekStart,
          end: this.currentWeekEnd
        };

        // If the user is a teacher or viewing as a teacher, add the teacher's ID to the params
        if (localStorage.getItem('role') === 'teacher' ||
            localStorage.getItem('role') === 'senior_teacher') {
          params.teacherId = localStorage.getItem('userId'); // Assuming the teacher's ID is stored in localStorage
        } else if (this.viewAsTeacher) {
          params.teacherId = this.selectedTeacher;
        }

        const [lessonsResponse, availableTimesResponse] = await Promise.all([
          axiosInstance.get('/admin/lessons', {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            },
            params
          }),
          axiosInstance.get('/admin/available-times', {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            },
            params
          })
        ]);

        // Populate the events array with lessons
        this.events = lessonsResponse.data.map(lesson => {
          const lessonDate = new Date(lesson.lesson_date); // Convert to Date object
          const lessonDateString = lessonDate.toLocaleDateString('en-US', { timeZone: 'Europe/Kiev' }); // Adjust to local format
          const startTime = lesson.start_time.split('+')[0].slice(0, 5); // Get HH:mm only
          const endTime = lesson.end_time.split('+')[0].slice(0, 5); // Get HH:mm only

          // Ensure valid formatting to "YYYY-MM-DD HH:mm"
          const startDateTimeStr = `${lessonDateString} ${startTime}`;  // Format without 'Z'
          const endDateTimeStr = `${lessonDateString} ${endTime}`;      // Format without 'Z'
          const uniqueClass = `event-${lesson.id}`;
          return {
            id: lesson.id,
            title: lesson.studygroup_name || this.$t('schedulesCalendar.noGroupName'),
            start: startDateTimeStr, // Correctly formatted date-time
            end: endDateTimeStr,     // Correctly formatted date-time
            class: `lesson-event ${uniqueClass}`, // If you're using a custom class
            lesson_completed: lesson.lesson_completed,
          };
        });
        // Populate the events array with available times
       // Populate the events array with available times
      availableTimesResponse.data.forEach(availableTime => {
        try {
          // Fix timezone issue by adding time component and specifying timezone
          const availableDate = new Date(`${availableTime.date}T12:00:00`);
          
          // Use date components directly to avoid timezone issues
          const year = availableDate.getFullYear();
          const month = availableDate.getMonth() + 1;
          const day = availableDate.getDate();
          const availableDateString = `${month}/${day}/${year}`;
          
          // Add null/undefined checks for the correct field names
          const startTime = availableTime.start_time ? 
            availableTime.start_time.split('+')[0].slice(0, 5) : '00:00';
            
          const endTime = availableTime.end_time ? 
            availableTime.end_time.split('+')[0].slice(0, 5) : '00:00';

          // Ensure valid formatting to "YYYY-MM-DD HH:mm"
          const startDateTimeStr = `${availableDateString} ${startTime}`;
          const endDateTimeStr = `${availableDateString} ${endTime}`;
          
          this.events.push({
            id: availableTime.id,
            title: this.$t('schedulesCalendar.freeTimeSlot'),
            start: startDateTimeStr,
            end: endDateTimeStr,
            class: 'available-time',
            content: 'Available',    
            background: false
          });
        } catch (error) {
          console.warn('Skipped invalid available time:', availableTime, error);
        }
      });

      } catch (error) {
        console.error('Error fetching lessons and available times:', error);
      }
    }
  },
   handleWeekChange(view) {

    // Use the start date of the week as the reference point
    const startDate = new Date(view.startDate); // Get the startDate from the view
    startDate.setHours(0, 0, 0, 0); // Reset to midnight (optional)

    this.getWeekStartAndEnd(startDate); // Determine week range based on displayed startDate

   this.fetchLessonsForCurrentWeek()
        .then(() => {
        }); // Log the events after fetching
},
    openForm() {
    this.isFormVisible = true; // Show the form modal
    //this.resetNewLesson();
    document.body.classList.add('modal-open');
  },
  
  closeForm() {
    this.isFormVisible = false; // Hide the form modal
    this.resetNewLesson(); // Optionally reset the form fields
    this.teacherError = false;
    document.body.classList.remove('modal-open');
  },
  openAvailableTimeForm() {
    this.isAvailableTimeFormVisible = true;
    //this.resetNewAvailableTime();
    document.body.classList.add('modal-open');
  },
  closeAvailableTimeForm() {
    this.isAvailableTimeFormVisible = false;
    this.resetNewAvailableTime();
    document.body.classList.remove('modal-open');
  },
  scrollToLessonDate(lessonDate) {
    const startOfWeek = new Date(lessonDate);
    const dayOfTheWeek = startOfWeek.getDay(); // Sunday is 0, Saturday is 6
    startOfWeek.setDate(lessonDate.getDate() - dayOfTheWeek + 1); // Adjust to Monday

    const calendar = this.$refs.vueCal.$el.querySelector('.vuecal__bg'); // Use ref to get correct element

    // Needed to recalculate the scrolling position
    const hours = 0; // If we want to scroll to the start of the day
    const timeCellHeight = 26; // Match this with your actual time cell height setting

    // Adjust the scroll position calculation as appropriate and scroll to the top of the selected week
    const topPosition = (dayOfTheWeek * 24 * timeCellHeight) + (hours * timeCellHeight);

    calendar.scrollTo({ top: topPosition, behavior: 'smooth' });
  },

  async addLesson() {
    if (!this.newLesson.teacherId || !this.newLesson.teacherName) {
      this.showNotification('error', this.$t('schedulesCalendar.teacherRequired'));
      return;
    }
    if (!this.newLesson.studyGroupName) {
      this.showNotification('error', this.$t('schedulesCalendar.groupRequired'));
      return;
    }
    if (!this.newLesson.title) {
      this.showNotification('error', this.$t('schedulesCalendar.titleRequired'));
      return;
    }
    if (!this.newLesson.billingPlanId) {
      this.showNotification('error', this.$t('schedulesCalendar.billingPlanRequired'));
      return;
    }

    let lessonAdded = false;

    if (this.lessonType === 'single') {
      lessonAdded = await this.createSingleLesson();
    } else if (this.lessonType === 'regular') {
      lessonAdded = await this.createRegularLessons();
    }

    if (lessonAdded) {
      // Construct a new event object
      const newEvent = {
        title: this.newLesson.title,
        start: `${this.newLesson.date.split('/').reverse().join('-')}T${this.newLesson.startTime}`, // Changes here
        end: `${this.newLesson.date.split('/').reverse().join('-')}T${this.newLesson.endTime}`, // Changes here
        class: 'lesson-event',
      };

      // Add the event to the events array
      this.events.push(newEvent);
      
    }
    this.resetNewLesson();
    this.selectedDate = this.newLesson.date;
    await this.fetchLessonsForCurrentWeek();
    this.closeForm(); // Close the form
  },
  showNotification(type, message) {
    // Create notification data
    this.notificationType = type;
    this.notificationMessage = message;
    this.notificationVisible = true;
    // Auto-hide after 3 seconds
    setTimeout(() => {
      this.notificationVisible = false;
    }, 3000);
  },
displayWeek(lessonDate) {
  const startOfWeek = new Date(lessonDate);
    startOfWeek.setDate(lessonDate.getDate() - startOfWeek.getDay() + 1); // Set to Monday

    this.$refs.vueCal.setView('week', startOfWeek);
},
  async createSingleLesson() {
    // Create date objects reflecting local time using startTime and endTime
    const localStartDate = new Date(`${this.newLesson.date.split('/').reverse().join('-')}T${this.newLesson.startTime}`); // Change here
    const localEndDate = new Date(`${this.newLesson.date.split('/').reverse().join('-')}T${this.newLesson.endTime}`); // Change here

    // Convert to UTC before sending to backend
    const newLessonData = {
      title: this.newLesson.title,
      studyGroup: this.studyGroupId,
      teacher: this.newLesson.teacherId,
      start: new Date(localStartDate.getTime() - (localStartDate.getTimezoneOffset() * 60000)).toISOString(), // Convert to UTC
      end: new Date(localEndDate.getTime() - (localEndDate.getTimezoneOffset() * 60000)).toISOString(), // Convert to UTC
      billingPlanId: this.newLesson.billingPlanId, // Include billing plan ID
    };

    try {
      const lessonResponse = await axiosInstance.post('/admin/add-lesson', newLessonData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (lessonResponse.status === 200) {
        const lessonId = lessonResponse.data.id;  // Assume the response contains the new lesson ID

        // Step 1: Fetch students based on study group ID
        const studentsResponse = await axiosInstance.get('/admin/students', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          params: {
            groupId: this.studyGroupId // Fetch students for the selected group
          }
        });

        // Step 2: Create attendance records for each student fetched
        for (const student of studentsResponse.data) {
          await axiosInstance.post('/admin/add-attendance', {
            lesson_id: lessonId, // Reference to the new lesson ID
            student_id: student.id, // Student's ID
            attended: false // Default to true or based on your logic
          }, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          });
        }

        // Add the new lesson event to the calendar
        this.events.push({
          title: `${this.newLesson.title}`,
          start: newLessonData.start,
          end: newLessonData.end,
          class: 'lesson-event',
        });

        // Remove the overlapping available time slot
        this.events = this.events.filter(event => {
          if (event.title === 'Free Time Slot') {
            const eventStart = new Date(event.start);
            const eventEnd = new Date(event.end);
            const lessonStart = new Date(newLessonData.start);
            const lessonEnd = new Date(newLessonData.end);
            return !(lessonStart < eventEnd && lessonEnd > eventStart);
          }
          return true;
        });

      } else {
        console.warn('Unexpected status returned from lesson update');
      }

    } catch (error) {
      console.error('Error adding lesson:', error);
    }
  },
  async createRegularLessons() {
    // Create date objects reflecting local time using startTime and endTime
    const localStartDate = new Date(`${this.newLesson.date.split('/').reverse().join('-')}T${this.newLesson.startTime}`); // Change here
    const localEndDate = new Date(`${this.newLesson.date.split('/').reverse().join('-')}T${this.newLesson.endTime}`); // Change here

    for (let i = 0; i < this.numberOfLessons; i++) {
      const lessonStartDate = new Date(localStartDate); // Clone start date
      const lessonEndDate = new Date(localEndDate); // Clone end date

      lessonStartDate.setDate(localStartDate.getDate() + (i * 7));
      lessonEndDate.setDate(localEndDate.getDate() + (i * 7));

      const newLessonData = {
        title: this.newLesson.title,
        studyGroup: this.studyGroupId,
        teacher: this.newLesson.teacherId,
        start: new Date(lessonStartDate.getTime() - (lessonStartDate.getTimezoneOffset() * 60000)).toISOString(),
        end: new Date(lessonEndDate.getTime() - (lessonEndDate.getTimezoneOffset() * 60000)).toISOString(),
        billingPlanId: this.newLesson.billingPlanId, // Include billing plan ID
      };

      try {
        const lessonResponse = await axiosInstance.post('/admin/add-lesson', newLessonData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (lessonResponse.status === 200) {
          const lessonId = lessonResponse.data.id; // Get the newly created lesson ID

          // Step 1: Fetch students based on study group ID
          const studentsResponse = await axiosInstance.get('/admin/students', {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            },
            params: {
              groupId: this.studyGroupId
            }
          });

          // Step 2: Create attendance records for each student fetched
          for (const student of studentsResponse.data) {
            await axiosInstance.post('/admin/add-attendance', {
              lesson_id: lessonId, // Reference to the new lesson ID
              student_id: student.id, // Each student's ID
              attended: false // Default attendance status
            }, {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            });
          }

          // Add the new lesson event to the calendar
          this.events.push({
            title: `${this.newLesson.title}`,
            start: newLessonData.start,
            end: newLessonData.end,
            class: 'lesson-event',
          });

          // Remove the overlapping available time slot
          this.events = this.events.filter(event => {
            if (event.title === 'Free Time Slot') {
              const eventStart = new Date(event.start);
              const eventEnd = new Date(event.end);
              const lessonStart = new Date(newLessonData.start);
              const lessonEnd = new Date(newLessonData.end);
              return !(lessonStart < eventEnd && lessonEnd > eventStart);
            }
            return true;
          });

        } else {
          console.warn('Unexpected status returned from lesson update');
        }

      } catch (error) {
        console.error('Error adding lesson:', error);
      }
    }
  },
  resetNewLesson() {
    this.newLesson = {
        title: '',
        date: '',          // Ensure this is initialized as an empty string
        startTime: '',     // Ensure this is initialized as an empty string
        endTime: '',       // Ensure this is initialized as an empty string
        teacherName: '',
        studyGroupName: '',
        billingPlanId: null,
    };
  },
  resetNewAvailableTime() {
    this.newAvailableTime = {
      day: '',
      startTime: '',
      endTime: '',
    };
  },
  onCellClick(cell) {
    const currentView = this.$refs.vueCal?.view.id;
  
    // If not in week view, navigate to the clicked date
    if (!this.$refs.vueCal || currentView !== 'week') {
      // Determine the date to navigate to
      let targetDate;
      
      if (cell instanceof Date) {
        targetDate = cell;
      } else if (cell && cell.date) {
        targetDate = new Date(cell.date);
      } else if (cell && cell.start) {
        targetDate = new Date(cell.start);
      }
      
      // If we have a valid date, navigate to it in the appropriate view
      if (targetDate) {
        if (currentView === 'year') {
          // From year view, go to month view
          this.$refs.vueCal.switchView('month', targetDate);
        } else if (currentView === 'month') {
          // From month view, go to week view
          this.$refs.vueCal.switchView('week', targetDate);
        } else if (currentView === 'day') {
          // From day view, go to week view containing this day
          this.$refs.vueCal.switchView('week', targetDate);
        }
      }
      
      // Exit the method as we don't want to show forms
      return;
    }
    let date, time, events;
    
    // Handle when cell is a Date object (this is happening in your case)
    if (cell instanceof Date) {
      // Extract date and time from the Date object
      date = cell.toISOString().split('T')[0]; // YYYY-MM-DD
      
      // Format time as HH:MM - but we'll round to hour blocks later
      const hours = cell.getHours().toString().padStart(2, '0');
      const minutes = cell.getMinutes().toString().padStart(2, '0');
      time = `${hours}:${minutes}`;
      
      // Empty events for a new time slot
      events = [];
    }
    // Original logic for handling objects with properties
    else if (cell) {
      // Check if we have cell.date
      if (cell.date) {
        date = cell.date;
        
        if (cell.time) {
          time = cell.time;
        } else if (cell.startTime) {
          time = cell.startTime;
        }
        
        events = cell.events || [];
      } 
      else if (cell.start) {
        const startDate = new Date(cell.start);
        date = startDate.toISOString().split('T')[0];
        time = startDate.toTimeString().substring(0, 5);
        events = [];
      }
    }
    
    console.log('Processed cell data:', { date, time, events });
    
    // Rest of your function remains the same
    if (date && time) {
      // Format the date for the form (dd/mm/yyyy)
      const clickedDate = new Date(date);
      const day = clickedDate.getDate().toString().padStart(2, '0');
      const month = (clickedDate.getMonth() + 1).toString().padStart(2, '0');
      const year = clickedDate.getFullYear();
      const formattedDate = `${day}/${month}/${year}`;
      
      // NEW: Round to full hours for both start and end times
      let [hours] = time.split(':').map(Number);
      
      // Create start time at the beginning of the hour
      const formattedStartTime = `${hours.toString().padStart(2, '0')}:00`;
      
      // Create end time exactly one hour later
      const endHour = (hours + 1) % 24; // Handle hour rollover
      const formattedEndTime = `${endHour.toString().padStart(2, '0')}:00`;
      
      if (this.isAdmin) {
        // First reset form to clear any old values
        this.resetNewLesson();
        
        // THEN pre-fill the new lesson form BEFORE showing the form
        this.newLesson.date = formattedDate;
        this.newLesson.startTime = formattedStartTime;
        this.newLesson.endTime = formattedEndTime;
        
        // Now show the form
        this.$nextTick(() => {
          this.isFormVisible = true; 
          document.body.classList.add('modal-open');
          
          // Force flatpickr to update after the form is visible
          this.$nextTick(() => {
            // Find the flatpickr instances and update them manually
            const dateInput = document.querySelector('input[placeholder="' + this.$t('schedulesCalendar.selectDate') + '"]');
            const startTimeInput = document.querySelector('input[placeholder="' + this.$t('schedulesCalendar.selectStartTime') + '"]');
            const endTimeInput = document.querySelector('input[placeholder="' + this.$t('schedulesCalendar.selectEndTime') + '"]');
            
            if (dateInput && dateInput._flatpickr) dateInput._flatpickr.setDate(formattedDate);
            if (startTimeInput && startTimeInput._flatpickr) startTimeInput._flatpickr.setDate(formattedStartTime);
            if (endTimeInput && endTimeInput._flatpickr) endTimeInput._flatpickr.setDate(formattedEndTime);
          });
        });
      } else if (this.isTeacher || this.isSeniorTeacher) {
        // First reset form to clear any old values
        this.resetNewAvailableTime();
        
        // THEN pre-fill the available time form
        let dayIndex = clickedDate.getDay() - 1;
        if (dayIndex < 0) dayIndex = 6;
        
        this.newAvailableTime.day = this.daysOfWeek[dayIndex];
        this.newAvailableTime.startTime = formattedStartTime;
        this.newAvailableTime.endTime = formattedEndTime;
        
        // Now show the form
        this.$nextTick(() => {
          this.isAvailableTimeFormVisible = true;
          document.body.classList.add('modal-open');
          
          // Force flatpickr to update after the form is visible
          this.$nextTick(() => {
            const startTimeInput = document.querySelectorAll('input[placeholder="' + this.$t('schedulesCalendar.selectStartTime') + '"]')[1]; // Second instance for available time
            const endTimeInput = document.querySelectorAll('input[placeholder="' + this.$t('schedulesCalendar.selectEndTime') + '"]')[1]; // Second instance for available time
            
            if (startTimeInput && startTimeInput._flatpickr) startTimeInput._flatpickr.setDate(formattedStartTime);
            if (endTimeInput && endTimeInput._flatpickr) endTimeInput._flatpickr.setDate(formattedEndTime);
          });
        });
      }
    } else {
      console.warn('Could not determine date and time from cell click');
    }
  },
  async addAvailableTime() {
    const { day, startTime, endTime } = this.newAvailableTime;
    const teacherId = localStorage.getItem('userId');
    
    // Find the day index in our array (0=Monday, 1=Tuesday, etc.)
    const dayIndex = this.daysOfWeek.indexOf(day);
    
    // Convert to JavaScript's day of week (0=Sunday, 1=Monday, etc.)
    // Since your array starts with Monday, we need to convert:
    // Monday(0) → 1, Tuesday(1) → 2, ..., Sunday(6) → 0
    const dayOfWeek = (dayIndex + 1) % 7;
    
    // Send just one entry with the dayOfWeek value
    const availableTimeData = [{
      teacherId,
      dayOfWeek, // This is what the backend needs - a number between 0-6
      startTime,
      endTime
    }];

    try {
      await axiosInstance.post('/admin/add-available-time', availableTimeData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      this.fetchLessonsForCurrentWeek();
      this.closeAvailableTimeForm();
    } catch (error) {
      console.error('Error adding available time:', error);
    }
  },
  getNextDate(day, weeksAhead) {
    const today = new Date();
    const dayIndex = this.daysOfWeek.indexOf(day);
    const currentDayIndex = today.getDay();
    const daysUntilNext = (dayIndex - currentDayIndex + 7) % 7;
    const nextDate = new Date(today);
    nextDate.setDate(today.getDate() + daysUntilNext + weeksAhead * 7);
    return nextDate.toISOString().split('T')[0];
  },
  editEvent(event) {
    this.newLesson.title = event.title;
    const [date, time] = event.start.split('T');
    this.newLesson.date = date;
    this.newLesson.startTime = time; // Set the start time from event data
    this.newLesson.endTime = event.end.split('T')[1]; // Extract end time
    this.openForm();
  },
  updateAvailableTimeSlot() {
    this.pendingEventData = {...this.editingAvailableTime};
    this.pendingEventType = 'availableTime';
    this.isDeleting = false;
    this.pendingOperation = 'edit';
    
    // Show the recurrence dialog
    this.showRecurrenceDialog = true;
  },

  async updateAvailableTimeWithRecurrence() {
      try {
        // Add recurrence type to the request
        const updatedData = {
          ...this.pendingEventData,
          recurrenceType: this.recurrenceType
        };
        
        await axiosInstance.put('/admin/update-available-time', updatedData, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        this.showAvailableTimeEditForm = false;
        this.fetchLessonsForCurrentWeek();
      } catch (error) {
        console.error('Error updating available time slot:', error);
        alert('Error updating available time slot. Please try again.');
      }
    },

    // Replace your deleteAvailableTimeSlot method
    deleteAvailableTimeSlot() {
      this.pendingEventData = {...this.editingAvailableTime};
      this.pendingEventType = 'availableTime';
      this.isDeleting = true;
      this.pendingOperation = 'delete';
      
      // Show the recurrence dialog
      this.showRecurrenceDialog = true;
    },

    // Add method for deleting available time slots with recurrence
    async deleteAvailableTimeWithRecurrence() {
      try {
        await axiosInstance.delete(`/admin/delete-available-time/${this.pendingEventData.id}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          },
          params: {
            recurrenceType: this.recurrenceType
          }
        });
        
        this.showAvailableTimeEditForm = false;
        this.fetchLessonsForCurrentWeek();
      } catch (error) {
        console.error('Error deleting available time slot:', error);
        alert('Error deleting available time slot. Please try again.');
      }
    }

  },
  mounted() {
    const today = new Date();
    this.getWeekStartAndEnd(today); // Initialize with the current week
    this.fetchLessonsForCurrentWeek();
    this.fetchBillingPlans();
    const storedDate = localStorage.getItem('lessonDate');
    if (storedDate) {
      this.newLesson.date = storedDate;
    }
  },
};
</script>

<style>
/* Base Layout */
.container.mt-5 {
  padding: 0;
  max-width: 1200px;
  margin: 0 auto 40px;
}

/* Modern Header */
.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eaeaea;
}

.calendar-header h2 {
  font-size: 1.75rem;
  font-weight: 600;
  color: #222;
  margin: 0;
}

.calendar-actions {
  display: flex;
  gap: 12px;
}

/* Button Styling */
.btn-primary {
  background: #4361ee;
  border: none;
  border-radius: 6px;
  padding: 10px 18px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: white;
  box-shadow: 0 2px 4px rgba(67, 97, 238, 0.25);
  margin-right: 10px;
}

.btn-primary:hover {
  background: #3a56d4;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(67, 97, 238, 0.3);
}

.btn-secondary {
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 9px 18px;
  font-weight: 500;
  transition: all 0.2s ease;
  color: #4b5563;
}

.btn-secondary:hover {
  background: #f9fafb;
  border-color: #9ca3af;
}

.btn-danger {
  background: #ef4444;
  border: none;
  border-radius: 6px;
  padding: 10px 18px;
  font-weight: 500;
  transition: all 0.2s ease;
  color: white;
  box-shadow: 0 2px 4px rgba(239, 68, 68, 0.25);
}

.btn-danger:hover {
  background: #dc2626;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(239, 68, 68, 0.3);
}

/* Button with icon */
.btn i {
  font-size: 0.9em;
}

/* Calendar Styling */
.vuecal {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
  border: 1px solid #eaeaea;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}

.vuecal__menu {
  background-color: #f8fafc;
  border-bottom: 1px solid #eaeaea;
  padding: 6px 16px;
}

.vuecal__title-bar {
  background-color: #fff;
  font-weight: 600;
  padding: 16px;
  border-bottom: 1px solid #eaeaea;
}

.vuecal__arrow {
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
}

.vuecal__arrow:hover {
  background-color: #f1f5f9;
}

/* Day headers */
.vuecal__heading {
  font-weight: 600;
  color: #4b5563;
  background-color: #f8fafc;
  border-bottom: 1px solid #eaeaea;
}

/* Time cells */
.vuecal__time-cell {
  transition: background-color 0.3s ease;
  border-color: #f1f5f9;
  position: relative;
  cursor: pointer;
}

.vuecal__time-cell:hover {
  background-color: rgba(67, 97, 238, 0.1) !important;
}

/* Time labels */
.vuecal__time-cell-label {
  font-size: 0.75rem;
  color: #64748b;
  font-weight: 500;
}

/* Cell content */
.vuecal__cell {
  position: relative;
  cursor: pointer;
}

.vuecal__cell-content {
  position: relative;
  z-index: 1;
}

/* Events */
.vuecal__event {
  border-radius: 4px;
  overflow: hidden;
  transition: all 0.2s;
  z-index: 2;
}

.vuecal__event:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

/* Regular lessons */
.vuecal__event.lesson-event {
  border-left: 3px solid #4361ee;
}

/* Available time styling */
.vuecal__event[class*="available-time"] {
  background-color: #fdeff2 !important;
  color: #9d174d !important;
  border: 1px dashed #fb7185 !important;
  border-radius: 4px;
}

.available-time {
  background-color: #fdeff2 !important;
  color: #9d174d !important;
  border: 1px dashed #fb7185 !important;
}

.vuecal__event.available-time {
  opacity: 0.8;
  cursor: pointer;
}

.vuecal__event.available-time:hover {
  opacity: 1;
  box-shadow: 0 2px 8px rgba(251, 113, 133, 0.3);
}

/* Overlay and Form Styling */
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.55);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
  overflow: auto;
  padding: 20px;
  backdrop-filter: blur(2px);
  transition: opacity 0.3s ease;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.form-popup {
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

/* Form Header */
.form-popup h2 {
  margin-top: 0;
  margin-bottom: 20px;
  font-size: 1.5rem;
  font-weight: 600;
  color: #222;
  border-bottom: 1px solid #eaeaea;
  padding-bottom: 16px;
}

/* Form Fields */
.form-popup .mb-3 {
  margin-bottom: 20px;
}
.mb-3 {
  position: relative; /* Add this */
}
.form-popup .form-label {
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #4b5563;
}

.form-popup .form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.95rem;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.form-popup .form-control:focus {
  border-color: #4361ee;
  box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.25);
  outline: none;
}

/* Form Actions */
.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 24px;
}

/* Radio and Checkbox Styling */
input[type="radio"],
input[type="checkbox"] {
  margin-right: 6px;
  accent-color: #4361ee;
}

input[type="radio"] + label,
input[type="checkbox"] + label {
  margin-right: 16px;
  cursor: pointer;
}

/* FlatPickr integration */
.flatpickr-input {
  background-color: white !important;
}

.flatpickr-calendar {
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  border: none !important;
}

/* Dropdown Suggestions */
.dropdown-suggestions {
  position: absolute;
  background: white;
  border: 1px solid #e5e7eb;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
  width: 100%; /* Keep this */
  max-width: 100%; /* Add this to prevent extending beyond container */
  border-radius: 6px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  margin-top: 4px;
  left: 0; /* Ensure it's aligned with the left edge of the parent */
  right: 0; /* Ensure it's contained within the parent width */
}

.dropdown-suggestions ul {
  list-style-type: none;
  padding: 0;
  margin: 0;
}

.dropdown-suggestions li {
  padding: 10px 12px;
  cursor: pointer;
  border-bottom: 1px solid #f3f4f6;
  transition: background-color 0.15s;
  white-space: nowrap; /* Prevent wrapping */
  overflow: hidden; /* Hide overflow */
  text-overflow: ellipsis; /* Show ellipsis for overflow text */
}

.dropdown-suggestions li:last-child {
  border-bottom: none;
}

.dropdown-suggestions li:hover {
  background-color: #f9fafb;
}

/* Recurrence Dialog */
.recurrence-dialog {
  max-width: 500px;
  width: 90%;
}

.recurrence-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
}

.recurrence-options .option {
  display: flex;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  align-items: center;
}

.recurrence-options .option:hover {
  background-color: #f9fafb;
  border-color: #d1d5db;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.option-icon {
  margin-right: 16px;
  font-size: 22px;
  color: #4361ee;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background-color: rgba(67, 97, 238, 0.1);
  border-radius: 50%;
}

.option-content {
  flex: 1;
}

.option-content h4 {
  margin: 0 0 4px 0;
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.option-content p {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}

/* Text styling */
.text-danger {
  color: #dc2626;
  font-size: 0.875rem;
  margin-top: 4px;
}

/* Loading UI */
.loader {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 2px solid rgba(67, 97, 238, 0.3);
  border-radius: 50%;
  border-top-color: #4361ee;
  animation: spin 1s ease-in-out infinite;
  margin-right: 10px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Utility for preventing body scroll when modal is open */
body.modal-open {
  overflow: hidden;
}

/* Responsive design */
@media (max-width: 768px) {
  .calendar-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
  
  .calendar-actions {
    width: 100%;
  }
  
  .btn-primary {
    flex: 1;
    justify-content: center;
  }
  
  .form-popup {
    padding: 16px;
    max-width: 95%;
  }
  
  .form-actions {
    flex-direction: column-reverse;
  }
  
  .form-actions button {
    width: 100%;
  }
  
  /* Make room for mobile touch targets */
  .vuecal__time-cell {
    min-height: 60px;
  }
}
.form-control,
select.form-control,
input.form-control {
  height: auto !important; 
  min-height: 44px;
  line-height: 1.5;
  padding: 10px 12px;
  white-space: normal;
}

/* Specific fix for billing plan dropdown/input */
[v-model="newLesson.billingPlanId"],
select[v-model="newLesson.billingPlanId"],
.mb-3 select {
  height: auto !important;
  min-height: 44px;
  padding-top: 10px;
  padding-bottom: 10px;
  line-height: 1.5;
}

/* For better spacing of option elements */
option {
  padding: 8px;
  line-height: 1.5;
}
</style>